<template>
    <v-container>
        <v-card v-if="wizardStep == 'upload'">
            <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
                Upload Gel Photos for {{ request.request_registry_id }}
            </v-card-title>
            <v-card-text>
                <v-row class="mx-12">
                    <v-col>
                        <DragDropFileUploader
                            class="mt-8"
                            :acceptedFileTypes="['image/tiff', 'image/png']"
                            @filesUploaded="uploadFiles($event)"
                            style="min-height: 240px; width: 100%"
                        />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="submitFiles" :disabled="files.length == 0" color="primary"
                    >Upload</v-btn
                >
                <v-btn @click="cancel" color="primary">Cancel</v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-if="wizardStep == 'mapContainers'">
            <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
                Map Gel Photos to Containers for {{ request.request_registry_id }}
            </v-card-title>
            <v-card-text>
                <MapFilesToContainers
                    :files="files"
                    :groupsToContainersMap="groupsToContainersMap"
                    containerType="plate"
                    @mappingChanged="mappingChanged($event)"
                />
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    @click="sendFilesToBackend"
                    :disabled="Object.values(containerMap).length === 0"
                    color="primary"
                    >Submit</v-btn
                >
                <v-btn @click="cancel" color="primary">Cancel</v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-if="wizardStep == 'review'">
            <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
                Upload Results
            </v-card-title>
            <v-card-text>
                <div style="padding-top: 8px">
                    <h3 v-if="!uploadsComplete">
                        Submitting {{ renamedFiles.length }} file(s)
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </h3>
                    <h3 v-if="uploadsComplete">
                        Completed submission of {{ renamedFiles.length }} file(s)
                        <v-icon :color="didAllUploadsSucceed() ? 'green' : 'red'">{{
                            didAllUploadsSucceed() ? "$check" : "$close"
                        }}</v-icon>
                    </h3>
                </div>
                <v-list>
                    <v-list-item v-for="file in renamedFiles" :key="file.name">
                        <v-list-item-title>
                            {{ file.name }}
                            <v-icon :color="getUploadStatusColor(file.name)">{{
                                getUploadStatusIcon(file.name)
                            }}</v-icon>
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="cancel" color="primary">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
import DragDropFileUploader from "@/components/common/DragDropFileUploader.vue";
import MapFilesToContainers from "@/components/common/MapFilesToContainers.vue";
import { enqueueBackgroundTaskAndPoll } from "@/lib/backgroundTasks";
import { DateTime } from "luxon";
import { S3Uploader } from "@/lib/s3Uploader";
import { mapGetters, mapActions } from "vuex";
import config from "@/config";

export default {
    name: "UploadRequestImagesWizard",
    components: { DragDropFileUploader, MapFilesToContainers },
    props: {
        request: Object,
    },
    data() {
        return {
            files: [],
            wizardStep: "upload",
            containerMap: {},
            groupsToContainersMap: {},
            uploadResults: [],
            uploadsComplete: false,
            renamedFiles: [],
        };
    },
    computed: {
        ...mapGetters({
            user: "getAuthUser",
        }),
    },
    methods: {
        ...mapActions(["refreshSession"]),
        cancel() {
            this.wizardStep = "upload";
            this.containerMap = {};
            this.groupsToContainersMap = {};
            this.files = [];
            this.uploadResults = [];
            this.$emit("close");
        },
        uploadFiles(files) {
            this.files = files;
        },
        async submitFiles() {
            // if there is only one plate, and only one file, skip the mapping step
            if (this.request.plate_names.length === 1 && this.files.length === 1) {
                this.containerMap[this.request.plate_names[0]] = this.files[0].name;
                await this.sendFilesToBackend();
            } else {
                this.groupsToContainersMap.Plates = this.request.plate_names;
                this.wizardStep = "mapContainers";
            }
        },
        mappingChanged(containerMap) {
            this.containerMap = containerMap;
        },
        async sendFilesToBackend() {
            this.renameFiles();
            await this.refreshSession();
            this.s3Uploader = new S3Uploader(this.user.idToken);
            const uploadPromises = this.renamedFiles.map((file) => {
                return this.sendFile(file, this.request.request_registry_id);
            });
            this.wizardStep = "review";
            this.uploadResults = await Promise.all(uploadPromises);
            this.uploadsComplete = true;
        },
        async sendFile(file, requestId) {
            let uploadStatus = "Succeeded";
            const filename = `${requestId}/${file.name}`;
            try {
                await this.s3Uploader.uploadFile(
                    config.requestImagesBucket,
                    filename,
                    file,
                    file.type
                );
            } catch (error) {
                console.error(error);
                uploadStatus = "Failed";
            }
            return { filename: file.name, status: uploadStatus };
        },
        renameFiles() {
            const dateString = DateTime.now().toFormat("yyyy-MM-dd");
            this.renamedFiles = Object.keys(this.containerMap).map((container) => {
                const file = this.files.find((file) => file.name === this.containerMap[container]);
                const extension = file.name.split(".").pop();
                if (this.containerMap[container]) {
                    const fileName = `${container}_X${dateString}Xgelphoto.${extension}`;
                    return new File([file], fileName, { type: `image/${extension}` });
                }
            });
        },
        getUploadStatusColor(filename) {
            const status = this.getUploadStatus(filename);
            switch (status) {
                case "Succeeded":
                    return "green";
                case "Failed":
                    return "red";
                default:
                    return "blue";
            }
        },
        getUploadStatusIcon(filename) {
            const status = this.getUploadStatus(filename);
            switch (status) {
                case "Succeeded":
                    return "$check";
                case "Failed":
                    return "$close";
                default:
                    return "$clock";
            }
        },
        getUploadStatus(filename) {
            const uploadResult = this.uploadResults.find((result) => result.filename === filename);
            return uploadResult ? uploadResult.status : "Pending";
        },
        didAllUploadsSucceed() {
            return this.uploadResults.every((result) => result.status === "Succeeded");
        },
    },
};
</script>
